// 🔒 LOCKED V1 — DO NOT MODIFY WITHOUT REVIEW
// Any new feature must be additive, not modifying existing structure

import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import useTranslation from '../../../hooks/useTranslation';
import PortalDropdown from '../../../components/ui/PortalDropdown';
import { extraDirectiveNumber } from '../../../utils/extraDirectiveNumber';
import useEscapeKey from '../../../hooks/useEscapeKey';

// ── helpers ───────────────────────────────────────────────────────────────────

const ALLOWED_TYPES = [
  'application/pdf',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/csv',
];

const ALLOWED_EXTENSIONS = ['pdf', 'xls', 'xlsx', 'doc', 'docx', 'csv'];

const sanitizeFilename = (name) =>
  name?.normalize('NFD')?.replace(/[\u0300-\u036f]/g, '')?.toLowerCase()
    ?.replace(/\s+/g, '_')?.replace(/[^a-z0-9_.]/g, '')?.replace(/_+/g, '_');

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024)?.toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024))?.toFixed(1)} MB`;
};

const getFileTypeIcon = (fileName) => {
  const ext = fileName?.split('.')?.pop()?.toLowerCase();
  if (ext === 'pdf') return <Icon name="FileText" size={14} className="text-red-500 flex-shrink-0" />;
  if (['xls', 'xlsx', 'csv']?.includes(ext)) return <Icon name="FileSpreadsheet" size={14} className="text-green-600 flex-shrink-0" />;
  if (['doc', 'docx']?.includes(ext)) return <Icon name="FileText" size={14} className="text-blue-500 flex-shrink-0" />;
  return <Icon name="File" size={14} className="text-gray-400 flex-shrink-0" />;
};

// Shared input class — matches AddContractModal exactly
const inputCls =
  'w-full px-3 py-2 text-xs border border-gray-200 rounded-lg outline-none ' + 'focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white transition-colors '+ 'disabled:opacity-50 disabled:cursor-not-allowed';

// ─────────────────────────────────────────────────────────────────────────────

const AddChangeOrderModal = ({ isOpen, onClose, onSubmit, projectId, subcontracts, projectExtras, editData }) => {
  const { t } = useTranslation();
  // Le champ saisi par le charge de projet vit dans la colonne notes.
  // La colonne description est le TITRE, calcule depuis l'extra source —
  // personne ne le tape.
  const [form, setForm] = useState({
    subcontractId: '',
    sourceExtraId: '',
    notes: '',
    amount: '',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Subcontract combobox state
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const comboboxRef = useRef(null);

  // Source Extra combobox state
  const [extraSearchTerm, setExtraSearchTerm] = useState('');
  const [extraDropdownOpen, setExtraDropdownOpen] = useState(false);
  const extraComboboxRef = useRef(null);

  // Document upload state
  const [pendingFiles, setPendingFiles] = useState([]);
  const [fileError, setFileError] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      if (editData) {
        const selectedId = editData?.subcontractId || editData?.subcontract?.id || '';
        const matched = subcontracts?.find(s => s?.id === selectedId);
        const selectedExtraId = editData?.sourceExtraId || editData?.sourceExtra?.id || '';
        const matchedExtra = projectExtras?.find(ex => ex?.id === selectedExtraId);
        setForm({
          subcontractId: selectedId,
          sourceExtraId: selectedExtraId,
          notes: editData?.notes || '',
          amount: editData?.amount?.toString() || '',
        });
        setSearchTerm(matched?.subcontractor?.companyName || '');
        setExtraSearchTerm(matchedExtra ? (matchedExtra?.title || matchedExtra?.id) : '');
      } else {
        setForm({ subcontractId: '', sourceExtraId: '', notes: '', amount: '' });
        setSearchTerm('');
        setExtraSearchTerm('');
      }
      setErrors({});
      setDropdownOpen(false);
      setExtraDropdownOpen(false);
      setPendingFiles([]);
      setFileError(null);
      setIsDragOver(false);
    }
  }, [isOpen, editData]);

  // Close subcontract dropdown on outside click
  // Close source extra dropdown on outside click
  const validate = () => {
    const newErrors = {};
    if (!form?.subcontractId) newErrors.subcontractId = 'Subcontract is required';
    if (!form?.amount || isNaN(parseFloat(form?.amount))) newErrors.amount = 'Valid amount is required';
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      // 🔴 La colonne description N'EST JAMAIS ENVOYEE EN MODIFICATION.
      // update() passe la charge telle quelle a PostgREST : une cle presente est
      // ecrite, et un null efface la colonne.
      // Le formulaire envoyait description: valeur ou null, donc vider la zone
      // de texte d'un avenant
      // genere automatiquement effacait son titre calcule. L'omettre laisse la
      // colonne intacte.
      const payload = {
        projectId,
        subcontractId: form?.subcontractId,
        sourceExtraId: form?.sourceExtraId || null,
        notes: form?.notes?.trim() || null,
        amount: parseFloat(form?.amount) || 0,
      };

      // A LA CREATION SEULEMENT : un avenant manuel rattache a un extra recoit le
      // titre de cet extra, comme le fait le chemin automatique. Sans extra
      // source, la cle est omise — pas de titre invente.
      if (!editData && form?.sourceExtraId) {
        const sourceExtra = projectExtras?.find(ex => ex?.id === form?.sourceExtraId);
        if (sourceExtra?.title) payload.description = sourceExtra?.title;
      }

      await onSubmit?.(payload);
      onClose?.();
    } catch (err) {
      console.error('Error submitting change order:', err);
    } finally {
      setLoading(false);
    }
  };

  // Subcontract combobox logic
  const filteredSubcontracts = subcontracts?.filter(s => {
    const companyName = s?.subcontractor?.companyName || '';
    return companyName?.toLowerCase()?.includes(searchTerm?.toLowerCase());
  }) || [];

  const handleSelect = (s) => {
    setForm(prev => ({ ...prev, subcontractId: s?.id }));
    setSearchTerm(s?.subcontractor?.companyName || '');
    setDropdownOpen(false);
    setErrors(prev => ({ ...prev, subcontractId: undefined }));
  };

  const handleInputChange = (e) => {
    setSearchTerm(e?.target?.value);
    setDropdownOpen(true);
    if (!e?.target?.value) {
      setForm(prev => ({ ...prev, subcontractId: '' }));
    }
  };

  // Source Extra combobox logic
  const filteredExtras = projectExtras?.filter(ex => {
    const title = ex?.title || ex?.id || '';
    return title?.toLowerCase()?.includes(extraSearchTerm?.toLowerCase());
  }) || [];

  const handleExtraSelect = (ex) => {
    if (!ex) {
      setForm(prev => ({ ...prev, sourceExtraId: '' }));
      setExtraSearchTerm('');
    } else {
      setForm(prev => ({ ...prev, sourceExtraId: ex?.id }));
      setExtraSearchTerm(ex?.title || ex?.id || '');
    }
    setExtraDropdownOpen(false);
  };

  const handleExtraInputChange = (e) => {
    setExtraSearchTerm(e?.target?.value);
    setExtraDropdownOpen(true);
    if (!e?.target?.value) {
      setForm(prev => ({ ...prev, sourceExtraId: '' }));
    }
  };

  // ── File upload handlers ───────────────────────────────────────────────────

  const validateAndAddFiles = (fileList) => {
    setFileError(null);
    const MAX_SIZE = 20 * 1024 * 1024;
    const errs = [];
    const valid = [];

    Array.from(fileList || [])?.forEach(file => {
      const ext = file?.name?.split('.')?.pop()?.toLowerCase();
      if (!ALLOWED_EXTENSIONS?.includes(ext) && !ALLOWED_TYPES?.includes(file?.type)) {
        errs?.push(`"${file?.name}": Unsupported file type. Use PDF, Excel, or Word.`);
        return;
      }
      if (file?.size > MAX_SIZE) {
        errs?.push(`"${file?.name}": File too large (max 20MB).`);
        return;
      }
      valid?.push(file);
    });

    if (errs?.length) setFileError(errs?.join('\n'));
    if (valid?.length) {
      setPendingFiles(prev => {
        const existingNames = new Set(prev?.map(f => f?.name));
        const newFiles = valid?.filter(f => !existingNames?.has(f?.name));
        return [...prev, ...newFiles];
      });
    }
  };

  const handleFileInputChange = (e) => {
    validateAndAddFiles(e?.target?.files);
    if (fileInputRef?.current) fileInputRef.current.value = '';
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragOver(false);
    validateAndAddFiles(e?.dataTransfer?.files);
  };

  const handleDragOver = (e) => { e?.preventDefault(); setIsDragOver(true); };
  const handleDragEnter = (e) => { e?.preventDefault(); setIsDragOver(true); };
  const handleDragLeave = () => setIsDragOver(false);

  const removePendingFile = (fileName) => {
    setPendingFiles(prev => prev?.filter(f => f?.name !== fileName));
  };

  const handleCancel = () => {
    onClose?.();
  };

  // Echap = Annuler ; inactif pendant l'enregistrement.
  useEscapeKey(() => { handleCancel(); }, !!isOpen && !loading);

  if (!isOpen) return null;

  // Category label mapping (mirrors extras page FR labels)
  const categoryLabel = (cat) => {
    if (!cat) return '—';
    const key = `extras.category.${cat}`;
    const label = t(key);
    // If t() returns the key itself (missing), fall back to raw value
    return label === key ? cat : label;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      {/* Modal — formulaire : pas de fermeture au clic exterieur */}
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh] overflow-hidden" role="dialog" aria-modal="true">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-violet-50 flex items-center justify-center">
              <Icon name="GitBranch" size={18} className="text-violet-600" />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-gray-900 leading-snug truncate">
                {editData ? t('avenants.form.editTitle') : t('avenants.form.addTitle')}
              </h2>
              <p className="text-[11px] text-gray-500 truncate">
                {editData ? t('avenants.form.editSubtitle') : t('avenants.form.addSubtitle')}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleCancel}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors flex-shrink-0"
          >
            <Icon name="X" size={16} />
          </button>
        </div>

        {/* Form — body defilant + pied fixe */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">

          {/* ── Read-only source extra info (edit mode only, when sourceExtra exists) ── */}
          {editData && editData?.sourceExtra && (
            <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 space-y-2">
              <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                {t('avenants.form.sourceInfo')}
              </p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                {/* Titre de l'extra source */}
                <div className="col-span-2">
                  <p className="text-[10px] text-gray-400">{t('avenants.form.sourceExtraTitle')}</p>
                  <p className="text-xs font-medium text-gray-800">{editData?.sourceExtra?.title || '—'}</p>
                </div>
                {/* Directive */}
                <div>
                  <p className="text-[10px] text-gray-400">{t('avenants.form.directive')}</p>
                  <p className="text-xs font-medium text-gray-800">{editData?.sourceExtra?.division || '—'}</p>
                </div>
                {/* Numéro de directive (client) */}
                <div>
                  <p className="text-[10px] text-gray-400">{t('avenants.form.directiveNumber')}</p>
                  <p className="text-xs font-medium text-gray-800">{extraDirectiveNumber(editData?.sourceExtra) || '—'}</p>
                </div>
                {/* Catégorie */}
                <div>
                  <p className="text-[10px] text-gray-400">{t('avenants.form.category')}</p>
                  <p className="text-xs font-medium text-gray-800">{categoryLabel(editData?.sourceExtra?.category)}</p>
                </div>
              </div>
            </div>
          )}

          {/* Subcontract */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Subcontract <span className="text-red-500">*</span>
            </label>
            <div className="relative" ref={comboboxRef}>
              <input
                type="text"
                value={searchTerm}
                onChange={handleInputChange}
                onFocus={() => setDropdownOpen(true)}
                placeholder="Select a subcontract..."
                autoComplete="off"
                className={`${inputCls} ${errors?.subcontractId ? 'border-red-400' : ''}`}
              />
              <PortalDropdown
                anchorRef={comboboxRef}
                open={dropdownOpen && (filteredSubcontracts?.length > 0 || !!searchTerm)}
                onClose={() => setDropdownOpen(false)}
                align="left"
                width={comboboxRef?.current?.offsetWidth || 0}
              >
                {filteredSubcontracts?.length > 0 ? (
                  <ul className="max-h-60 overflow-y-auto">
                    {filteredSubcontracts?.map(s => (
                      <li
                        key={s?.id}
                        onMouseDown={() => handleSelect(s)}
                        className={`px-3 py-2 text-xs cursor-pointer hover:bg-orange-50 hover:text-primary ${form?.subcontractId === s?.id ? 'bg-orange-50 text-primary font-medium' : 'text-gray-800'}`}
                      >
                        {s?.subcontractor?.companyName || 'Unknown'}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="px-3 py-2 text-xs text-gray-400">No results found</div>
                )}
              </PortalDropdown>
            </div>
            {errors?.subcontractId && <p className="text-xs text-red-500 mt-1">{errors?.subcontractId}</p>}
          </div>

          {/* Source Extra (optional) */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Source Extra <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <div className="relative" ref={extraComboboxRef}>
              <input
                type="text"
                value={extraSearchTerm}
                onChange={handleExtraInputChange}
                onFocus={() => setExtraDropdownOpen(true)}
                placeholder="None"
                autoComplete="off"
                className={inputCls}
              />
              <PortalDropdown
                anchorRef={extraComboboxRef}
                open={extraDropdownOpen}
                onClose={() => setExtraDropdownOpen(false)}
                align="left"
                width={extraComboboxRef?.current?.offsetWidth || 0}
              >
                <ul className="max-h-60 overflow-y-auto">
                  <li
                    onMouseDown={() => handleExtraSelect(null)}
                    className={`px-3 py-2 text-xs cursor-pointer hover:bg-orange-50 hover:text-primary italic ${!form?.sourceExtraId ? 'bg-orange-50 text-primary font-medium' : 'text-gray-400'}`}
                  >
                    None
                  </li>
                  {filteredExtras?.length > 0 ? (
                    filteredExtras?.map(ex => (
                      <li
                        key={ex?.id}
                        onMouseDown={() => handleExtraSelect(ex)}
                        className={`px-3 py-2 text-xs cursor-pointer hover:bg-orange-50 hover:text-primary ${form?.sourceExtraId === ex?.id ? 'bg-orange-50 text-primary font-medium' : 'text-gray-800'}`}
                      >
                        {extraDirectiveNumber(ex) && (
                          <span className="mr-1.5 text-gray-500 font-medium">{extraDirectiveNumber(ex)} ·</span>
                        )}
                        {ex?.title || ex?.id}
                        {ex?.status && (
                          <span className="ml-1.5 text-gray-400 font-normal">({ex?.status})</span>
                        )}
                      </li>
                    ))
                  ) : extraSearchTerm ? (
                    <li className="px-3 py-2 text-xs text-gray-400 italic">No results found</li>
                  ) : null}
                </ul>
              </PortalDropdown>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">Description</label>
            <textarea
              value={form?.notes}
              onChange={e => setForm(prev => ({ ...prev, notes: e?.target?.value }))}
              placeholder="Describe the change order..."
              rows={3}
              className={`${inputCls} resize-none`}
            />
          </div>

          {/* Amount */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Amount <span className="text-red-500">*</span>
            </label>
            <div className="relative flex items-center">
              <span className="absolute left-3 text-xs text-gray-500 select-none pointer-events-none">$</span>
              <input
                type="number"
                step="0.01"
                value={form?.amount}
                onChange={e => setForm(prev => ({ ...prev, amount: e?.target?.value }))}
                placeholder="0.00"
                className={`${inputCls} pl-6 ${errors?.amount ? 'border-red-400' : ''}`}
              />
            </div>
            {errors?.amount && <p className="text-xs text-red-500 mt-1">{errors?.amount}</p>}
          </div>

          {/* Documents (create mode only) */}
          {!editData && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Documents <span className="text-gray-400 font-normal">(optional)</span>
              </label>

              {/* File error */}
              {fileError && (
                <div className="mb-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 whitespace-pre-line">
                  {fileError}
                </div>
              )}

              {/* Pending files list */}
              {pendingFiles?.length > 0 && (
                <ul className="mb-2 space-y-1">
                  {pendingFiles?.map((file) => (
                    <li key={file?.name} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-100">
                      {getFileTypeIcon(file?.name)}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-800 truncate">{file?.name}</p>
                        <p className="text-[10px] text-gray-400">{formatFileSize(file?.size)}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removePendingFile(file?.name)}
                        className="p-1 rounded text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors flex-shrink-0"
                        title="Remove file"
                      >
                        <Icon name="X" size={12} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {/* Upload drop zone */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.xls,.xlsx,.doc,.docx,.csv"
                className="hidden"
                onChange={handleFileInputChange}
              />
              <div
                onClick={() => fileInputRef?.current?.click()}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                className={`border-2 border-dashed rounded-xl px-4 py-5 text-center cursor-pointer transition-all ${
                  isDragOver
                    ? 'border-primary bg-orange-50 scale-[1.01]'
                    : 'border-gray-200 hover:border-primary hover:bg-orange-50/30'
                }`}
              >
                <div className="flex flex-col items-center gap-1.5">
                  <Icon name="Upload" size={18} className={isDragOver ? 'text-primary' : 'text-gray-400'} />
                  <p className="text-xs font-medium text-gray-600">
                    Drop files here or <span className="text-primary">click to upload</span>
                  </p>
                  <p className="text-[10px] text-gray-400">PDF, Excel, Word supported · Max 20MB each</p>
                </div>
              </div>
            </div>
          )}

        </div>

        {/* Footer — Cancel left, action right */}
        <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-gray-100 flex-shrink-0">
          <button
            type="button"
            onClick={handleCancel}
            disabled={loading}
            className="px-3.5 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-60"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-60"
          >
            {loading ? (
              <>
                <Icon name="Loader2" size={13} className="animate-spin" />
                {t('common.saving')}
              </>
            ) : (
              <>
                <Icon name="Check" size={13} />
                {editData ? t('avenants.form.saveChanges') : t('avenants.form.create')}
              </>
            )}
          </button>
        </div>
        </form>
      </div>
    </div>
  );
};

export default AddChangeOrderModal;
